import React from "react";

type Severity = 'normal' | 'warning' | 'critical';

interface StatCardProps {
  label: string;
  value: number | string;
  trend?: string;
  severity?: Severity;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const severityClasses: Record<Severity, string> = {
  normal: "bg-surface-raised border-edge",
  warning: "bg-warning-subtle border-warning-border",
  critical: "bg-danger-subtle border-danger-border",
};

const severityValueClasses: Record<Severity, string> = {
  normal: "text-content",
  warning: "text-warning-fg",
  critical: "text-danger-fg",
};

export default function StatCard({
  label,
  value,
  trend,
  severity = 'normal',
  icon,
  onClick,
}: StatCardProps) {
  const cardClasses = `rounded-xl border p-5 shadow-sm flex flex-col gap-2 ${severityClasses[severity]} ${
    onClick ? "cursor-pointer hover:shadow-md transition-shadow duration-150" : ""
  }`;

  // A div carrying role="button" and tabIndex is reachable by keyboard but does
  // not activate on Enter or Space the way a real button does, so these cards
  // were focusable dead ends for anyone not using a mouse.
  const handleKeyDown = onClick
    ? (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-start justify-between">
        <span className="text-sm font-medium text-content-muted">{label}</span>
        {icon && <span className="text-content-subtle">{icon}</span>}
      </div>
      <div className={`text-3xl font-bold ${severityValueClasses[severity]}`}>{value}</div>
      {trend && <div className="text-xs text-content-subtle">{trend}</div>}
    </div>
  );
}
